import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { ValidationErrors } from 'webapp-types';
import { UserRole } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import { validateEmail } from 'webapp/utils/validate';

import validationMessage from 'webapp/helpers/validation-message';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';
import type { InviteUserData } from 'webapp/components/user/invite';

interface Args {
	onInvite: (data: InviteUserData[]) => void;
	onClose: () => void;
	submitInProgress: boolean;
}

export default class UserNonAdminInviteComponent extends Component<Args> {
	@tracked inviteesEmails = '';
	@tracked errors: ValidationErrors<'inviteesEmails'> = {};

	get emails(): string[] {
		const trimmed = this.inviteesEmails.trim();
		let emails: string[] = [];

		if (trimmed.length) {
			emails = trimmed.split(',');
		}

		return emails.map((email) => email.trim());
	}

	validateField(): boolean {
		const emails = this.emails;
		let emailErrorMsg = '';

		this.errors = {};

		if (emails.length) {
			emails.forEach((email) => {
				if (!validateEmail({ email }, 'email')) {
					emailErrorMsg = 'Invalid email';
				} else if (uniq(emails).length !== emails.length) {
					emailErrorMsg = 'Duplicated email';
				}
			});
		} else {
			emailErrorMsg = 'Missing email';
		}

		if (emailErrorMsg) {
			this.errors.inviteesEmails = [{ message: emailErrorMsg }];
		}

		return Object.keys(this.errors).length === 0;
	}

	@action
	invite(): void {
		if (this.validateField()) {
			const users: InviteUserData[] = this.emails.map((email) => ({
				email,
				level: UserRole.USER,
			}));

			this.args.onInvite(users);
		}
	}
	<template>
		<div class="row">
			<div class="label">
				Enter
				<strong>1 or more email addresses</strong>
				for users to be added to the account:
			</div>
			<FormInputComponent
				name="invited_user_emails"
				placeholder="Enter email separated by commas"
				disabled={{@submitInProgress}}
				@value={{this.inviteesEmails}}
				@hideFloatLabel={{true}}
			/>
			<RowErrorComponent @items={{this.errors.inviteesEmails}} />

			{{#if this.errors.inviteesEmails}}
				<div class="errorMessage skipFormError force_block">
					<CommonUiIconComponent @name="AlertFill" @size="sm" />
					<span class="message">{{validationMessage this.errors.inviteesEmails}}</span>
				</div>
			{{/if}}
		</div>

		<div class="row info">
			<p>
				New users will be
				<strong>pending until confirmed</strong>
				by one of the account’s administrators.
			</p>
		</div>

		<div class="controls2">
			<div class="controls2__right">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{@onClose}}
				/>
				<SubmitButtonComponent
					@text="Send invite to administrator(s)"
					@extraClass="save"
					@onTrigger={{this.invite}}
					@submitInProgress={{@submitInProgress}}
				/>
			</div>
		</div>
	</template>
}
